import { DiscoveryApi, FetchApi, OAuthApi } from '@backstage/core-plugin-api';
import { Config } from '@backstage/config';
import {
  AzurePolicyApi,
  PolicyStatesRequest,
  PolicyStatesResponse,
  AzureManagementGroupsWithSubscriptionsResponse,
} from './AzurePolicyApi';
import { getMicrosoftAuthScope } from '../utils';

export interface AzurePolicyClientOptions {
  discoveryApi: DiscoveryApi;
  fetchApi: FetchApi;
  microsoftAuthApi?: OAuthApi;
  config: Config;
}

export class AzurePolicyClient implements AzurePolicyApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;
  private readonly microsoftAuthApi?: OAuthApi;
  private readonly config: Config;

  constructor(options: AzurePolicyClientOptions) {
    this.discoveryApi = options.discoveryApi;
    this.fetchApi = options.fetchApi;
    this.microsoftAuthApi = options.microsoftAuthApi;
    this.config = options.config;
  }

  /**
   * Get the Azure access token for the current user.
   *
   * Requests a token with scopes for the backend application (e.g., 'api://<backend-app-id>/access-as-user').
   * The backend will then use the On-Behalf-Of (OBO) flow to exchange this token for an 
   * Azure Resource Manager token with the user's permissions.
   * 
   * Returns undefined if Microsoft auth is not configured, allowing graceful fallback to service principal.
   */
  private async getAzureToken(): Promise<string | undefined> {
    if (!this.microsoftAuthApi) {
      // Gracefully return undefined when Microsoft Auth is not available
      // This allows the backend to fall back to service principal authentication
      return undefined;
    }

    // Get the scopes from config (platt.azurePolicy.authScopes)
    const scopes = getMicrosoftAuthScope(this.config);

    if (scopes.length === 0) {
      // Gracefully return undefined when no scopes are configured
      // This allows the backend to fall back to service principal authentication
      return undefined;
    }

    try {
      // Request token for the backend application to enable OBO flow
      const token = await this.microsoftAuthApi.getAccessToken(scopes);
      return token;
    } catch (error) {
      // If token acquisition fails (e.g., user not authenticated), return undefined
      // This allows the backend to fall back to service principal authentication
      return undefined;
    }
  }

  async getManagementGroupsWithSubscriptions(): Promise<AzureManagementGroupsWithSubscriptionsResponse> {
    const abortController = new AbortController();

    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('az-policy');
      const azureToken = await this.getAzureToken();

      const headers: HeadersInit = {
        'content-type': 'application/json',
        accept: 'application/json',
      };

      // Add token to header only if available
      if (azureToken) {
        // Using X-Azure-Token to avoid conflict with Backstage's Authorization header
        headers['x-azure-token'] = azureToken;
      }

      const response = await this.fetchApi.fetch(
        `${baseUrl}/management-groups-with-subscriptions`,
        {
          method: 'GET',
          credentials: 'include',
          headers,
          signal: abortController.signal,
        },
      );

      if (!response.ok) {
        throw new Error(
          `Failed to fetch management groups with subscriptions: ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const result = await response.json();
      return result as AzureManagementGroupsWithSubscriptionsResponse;
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Connection timed out.');
      }
      throw error;
    }
  }

  async getPolicyStates(
    request: PolicyStatesRequest,
  ): Promise<PolicyStatesResponse> {
    const abortController = new AbortController();

    try {
      const baseUrl = await this.discoveryApi.getBaseUrl('az-policy');
      const azureToken = await this.getAzureToken();

      const headers: HeadersInit = {
        'content-type': 'application/json',
        accept: 'application/json',
      };

      // Add token to header only if available
      if (azureToken) {
        // Using X-Azure-Token to avoid conflict with Backstage's Authorization header
        headers['x-azure-token'] = azureToken;
      }

      const response = await this.fetchApi.fetch(`${baseUrl}/policy-states`, {
        method: 'POST',
        body: JSON.stringify(request),
        credentials: 'include',
        headers,
        signal: abortController.signal,
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch policy states: ${response.status} ${response.statusText}`,
        );
      }

      if (!response.body) {
        throw new Error('Response body is null');
      }

      const result = await response.json();
      return result as PolicyStatesResponse;
    } catch (error: unknown) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        throw new Error('Connection timed out.');
      }
      throw error;
    }
  }
}
